import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  CreditCard,
  ChevronRight,
  User,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/context/useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/api/axios";

import CollapseIcon from "@/assets/collapse.svg";
import NotificationIcon from "@/assets/notification.svg";
import SettingsIcon from "@/assets/settings.svg";

import ProjectsIcon from "@/assets/projects.svg";
import ReportsIcon from "@/assets/reports.svg";
import GatewayIcon from "@/assets/gateway.svg";
import logo from "@/assets/logo.svg";

const NAV_ITEMS = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  {
    label: "Businesses",
    icon: ProjectsIcon,
    path: "/projects",
    hasSubmenu: true,
    isAsset: true,
  },
  {
    label: "Reports",
    icon: ReportsIcon,
    path: "/reports",
    isAsset: true,
  },
  {
    label: "Gateway Monitor",
    icon: GatewayIcon,
    path: "/gateway-monitor",
    isAsset: true,
  },
  { label: "Payments", icon: CreditCard, path: "/payments" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null);
  const activeProjectId = (() => {
    const m = location.pathname.match(/^\/projects\/([^/]+)/);
    return m ? m[1] : null;
  })();

  const [openProfile, setOpenProfile] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState(false);
  const [projects, setProjects] = useState([]);

 
  useEffect(() => {
    document.body.dataset.sidebar = collapsed ? "collapsed" : "expanded";
  }, []);


  useEffect(() => {
  function handleClickOutside(event) {
    if (profileRef.current && !profileRef.current.contains(event.target)) {
      setOpenProfile(false);
    }
  }

  if (openProfile) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [openProfile]);
 
  useEffect(() => {
    let mounted = true;
    const fetchProjects = async () => {
      try {
        const res = await api.get("/api/projects");
        if (mounted) setProjects(res.data.data || []);
      } catch (err) {
        console.error("Failed to load projects", err);
      }
    };
    fetchProjects();
    return () => {
      mounted = false;
    };
  }, []);

  const asideRef = useRef(null);

  
  const handleTransitionEnd = useCallback((e) => {
    if (e.target === asideRef.current && e.propertyName === 'width') {
      document.body.dataset.sidebar = collapsed ? "collapsed" : "expanded";
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      document.body.dataset.sidebar = next ? "collapsed" : "expanded";
      return next;
    });
  }, []);


  const handleClickCapture = useCallback((e) => {
    if (collapsed) {
      e.stopPropagation();
      e.preventDefault();
      setCollapsed(false);
      document.body.dataset.sidebar = "expanded";
    }
  }, [collapsed]);

  const navItems = NAV_ITEMS;

  return (
    <aside
      ref={asideRef}
      onClickCapture={handleClickCapture}
      onTransitionEnd={handleTransitionEnd}
      style={{ willChange: 'width', transitionProperty: 'width', transitionDuration: '300ms' }}
      className={`fixed left-4 top-4 bottom-4 z-30 ${
        collapsed ? "w-[72px]" : "w-[220px]"
      }`}
    >
      <div className="h-full bg-gray-100 rounded-xl border shadow-sm flex flex-col justify-between">

        
        <div>
          <div className={`px-3 ${collapsed ? 'flex flex-col items-center gap-4 py-4' : 'h-13 flex items-center justify-between'}`}>
            <img src={logo} className="w-auto h-8" onClick={() => navigate("/")} />
            
            {collapsed ? (
              <button
                onClick={toggleCollapsed}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                onClick={toggleCollapsed}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <img src={CollapseIcon} className="w-auto h-5" />
              </button>
            )}
          </div>

          <nav className={`px-2 space-y-1 ${collapsed ? 'py-1' : 'py-3'}`}>
            {navItems.map((item) => (
              <div key={item.label}>
                <NavLink
                  to={item.path}
                  onClick={(e) => {
                    if (item.hasSubmenu) {
                      e.preventDefault();
                      setExpandedProjects((p) => !p);
                    }
                  }}
                  className={({ isActive }) =>
                    `flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 py-2 rounded-lg text-sm transition ${
                      isActive
                        ? "bg-blue-100 text-blue-900 font-medium"
                        : "text-black-900 hover:bg-gray-200"
                    }`
                  }
                >
                  <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                    {item.isAsset ? (
                      <img src={item.icon} className="w-5 h-5" />
                    ) : (
                      <item.icon size={18} />
                    )}
                    {!collapsed && item.label}
                  </div>

                  {!collapsed && item.hasSubmenu && (
                    <ChevronRight
                      size={16}
                      className={`transition ${
                        expandedProjects ? "rotate-90" : ""
                      }`}
                    />
                  )}
                </NavLink>

                
                {item.hasSubmenu && expandedProjects && !collapsed && (
                  <div className="ml-6 mt-1 space-y-1">
                    {projects.length === 0 ? (
                      <div className="text-xs text-black-900 px-2 py-1">
                        No projects
                      </div>
                    ) : (
                      projects.map((project) => {
                        const isActiveProject = String(activeProjectId) === String(project._id);
                        return (
                          <button
                            key={project._id}
                            onClick={() => navigate(`/projects/${project._id}`)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm ${isActiveProject ? 'bg-blue-100 text-blue-900 font-medium' : 'text-black-900 hover:bg-gray-50'}`}
                          >
                            {project.name}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

       
        <div className="px-2 pb-3 space-y-1">
          <button className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 py-2 rounded-lg text-sm text-black-900 hover:bg-gray-100`}>
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
              <img src={NotificationIcon} className="w-5 h-5" />
              {!collapsed && "Notifications"}
            </div>
            {!collapsed && <ChevronRight size={16} />}
          </button>

          <button className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 py-2 rounded-lg text-sm text-black-900 hover:bg-gray-100`}>
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
              <img src={SettingsIcon} className="w-5 h-5" />
              {!collapsed && "Settings"}
            </div>
            {!collapsed && <ChevronRight size={16} />}
          </button>

          <div ref={profileRef} className="mt-2 relative">
            <button
              onClick={() => setOpenProfile((p) => !p)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 ${collapsed ? 'justify-center' : ''}`}
            >
              {user?.avatar ? ( 
                <img
                  src={user.avatar}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <User size={16} className="text-gray-600" />
                </div>
              )}

              {!collapsed && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.name || "Account"}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    className={`transition ${
                      openProfile ? "rotate-90" : ""
                    }`}
                  />
                </>
              )}
            </button>

            {openProfile && !collapsed && (
  <div
    className="
      absolute
      left-full
      bottom-0
      ml-3
      w-56
      bg-white
      border
      rounded-xl
      shadow-lg
      z-50
    "
  >
   
    <div className="flex items-center gap-3 px-4 py-3">
      {user?.avatar ? (
        <img
          src={user.avatar}
          className="h-9 w-9 rounded-full object-cover"
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
          <User size={18} className="text-gray-600" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {user?.name || "Account"}
        </p>
      </div>
    </div>

    <div className="h-px bg-gray-200" />

   
    <button
      onClick={() => {
        logout();
        navigate("/");
      }}
      className="
        w-full
        px-4
        py-3
        text-sm
        text-black-600
        hover:bg-gray-50
        flex
        items-center
        gap-2
      "
    >
      <LogOut size={16} />
      Sign out
    </button>
  </div>
)}

          </div>
        </div>

      </div>
    </aside>
  );
}